import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DisabledUser } from "@/types/disabled-user.definition";

interface DisabledUsersState {
  users: DisabledUser[];
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
}

const initialState: DisabledUsersState = {
  users: [],
  loading: false,
  error: null,
  lastFetch: null,
};

export const fetchDisabledUsersRequest = createAction(
  "disabledUsers/fetchRequest"
);
export const fetchDisabledUsersSuccess = createAction<DisabledUser[]>(
  "disabledUsers/fetchSuccess"
);
export const fetchDisabledUsersFailure = createAction<string>(
  "disabledUsers/fetchFailure"
);

export const reactivateUserRequest = createAction<{ userId: number }>(
  "disabledUsers/reactivateRequest"
);
export const reactivateUserSuccess = createAction<number>(
  "disabledUsers/reactivateSuccess"
);
export const reactivateUserFailure = createAction<string>(
  "disabledUsers/reactivateFailure"
);

const disabledUsersSlice = createSlice({
  name: "disabledUsers",
  initialState,
  reducers: {
    clearDisabledUsersError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDisabledUsersRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDisabledUsersSuccess, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchDisabledUsersFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(reactivateUserRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        reactivateUserSuccess,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.users = state.users.filter(u => u.id !== action.payload);
          state.error = null;
        }
      )
      .addCase(reactivateUserFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDisabledUsersError } = disabledUsersSlice.actions;

export const selectDisabledUsers = (state: {
  disabledUsers: DisabledUsersState;
}) => state.disabledUsers.users;

export const selectDisabledUsersLoading = (state: {
  disabledUsers: DisabledUsersState;
}) => state.disabledUsers.loading;

export const selectDisabledUsersError = (state: {
  disabledUsers: DisabledUsersState;
}) => state.disabledUsers.error;

export const selectDisabledUsersIsDataStale = (state: {
  disabledUsers: DisabledUsersState;
}) => {
  if (!state.disabledUsers.lastFetch) return true;
  return Date.now() - state.disabledUsers.lastFetch > 5 * 60 * 1000;
};

export default disabledUsersSlice.reducer;
